import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight, ChevronDown, Crown, Play, Compass, NotebookPen, Sparkles, Flag, BookOpen } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import FourPillarsCheckIn from '../components/FourPillarsCheckIn';

// ═══════════════════════════════════════════════
// COACHING — The Four Pillars for Creating a Path to Wellness
// Collapsed from the original 9 Pillars into 4: The Body, The Mind,
// The Emotional, The Energetics. Deeper lesson content added over time.
// (Original 9-pillar write-ups preserved in git history.)
// ═══════════════════════════════════════════════

// Graphic for the Four Pillars (owner-supplied). Leave '' to show a placeholder.
const PILLARS_IMG = 'https://customer-assets-v7afamib.emergentagent.net/job_9cc27ad2-9473-4d8d-951e-838ca155566b/artifacts/w7o9r493_magic%20layers.png';

// category → colour accent
const CAT_STYLES = {
  Physical: { chip: 'bg-emerald-100 text-emerald-800', ring: 'border-emerald-200', bg: 'bg-emerald-50/60', badge: 'bg-emerald-600' },
  Mental: { chip: 'bg-amber-100 text-amber-800', ring: 'border-amber-200', bg: 'bg-amber-50/60', badge: 'bg-amber-600' },
  Emotional: { chip: 'bg-sky-100 text-sky-800', ring: 'border-sky-200', bg: 'bg-sky-50/60', badge: 'bg-sky-600' },
  Spiritual: { chip: 'bg-violet-100 text-violet-800', ring: 'border-violet-200', bg: 'bg-violet-50/60', badge: 'bg-violet-600' },
  Integration: { chip: 'bg-rose-100 text-rose-800', ring: 'border-rose-200', bg: 'bg-rose-50/60', badge: 'bg-rose-500' },
};

const lessons = [
  {
    num: 1,
    title: 'The Body',
    category: 'Physical',
    categoryLabel: 'Physical',
    body: [
      'Build the physical foundation that supports health, vitality and healing.',
    ],
    list: [
      { h: 'Nourish your body and support gut and metabolic health' },
      { h: 'Move, breathe, mobilize and restore healthy physical function' },
      { h: 'Support hydration, minerals, circulation and flow' },
      { h: 'Reduce the environmental burdens your body is navigating' },
      { h: "Support your body's natural pathways of digestion, elimination and recovery" },
    ],
    tags: ['Nutrition', 'Movement', 'Detox'],
  },
  {
    num: 2,
    title: 'The Mind',
    category: 'Mental',
    categoryLabel: 'Mental',
    body: [
      'The way we think shapes the way we experience ourselves, our health and our lives.',
    ],
    list: [
      { h: 'Notice the thoughts and inner dialogue running in the background' },
      { h: 'Explore beliefs and patterns that may be keeping you stuck' },
      { h: 'Build thoughts and beliefs that support where you want to go' },
      { h: 'Understand expectation, perception and the mind-body connection' },
      { h: 'Create a vision that gives your healing and growth direction' },
    ],
    tags: ['Beliefs', 'Perception', 'Vision'],
  },
  {
    num: 3,
    title: 'The Emotional',
    category: 'Emotional',
    categoryLabel: 'Emotional',
    body: [
      'Create greater harmony between what you feel, how you respond and what your body carries.',
    ],
    list: [
      { h: 'Build emotional well-being through greater awareness and understanding of what you feel' },
      { h: 'Strengthen emotional intelligence and self-awareness' },
      { h: 'Build greater capacity for regulation, expression and resilience' },
      { h: 'Unwind old emotional patterns and protective responses' },
      { h: 'Understand the language of emotions in the body' },
    ],
    tags: ['Awareness', 'Regulation', 'Resilience'],
  },
  {
    num: 4,
    title: 'The Energetic',
    category: 'Spiritual',
    categoryLabel: 'Energetic',
    body: [
      'Explore the less tangible layers of who you are and the energy you bring into your life.',
    ],
    list: [
      { h: 'Step into yourself as an energetic being, knowing you are more energy than body' },
      { h: 'Cultivate coherence through breath, presence and intentional practice' },
      { h: 'Explore consciousness, connection and your relationship to something greater' },
      { h: 'Harness your energy as a source of personal empowerment' },
      { h: 'Deepen your connection to intuition and inner guidance' },
    ],
    tags: ['Energy', 'Coherence', 'Intuition'],
  },
];

const influences = [
  {
    title: 'Hierarchy of Needs',
    text: "If basic safety, finances, housing, or rest are unstable, higher-level wellness behaviors become unrealistic. This isn't failure—it's physiology and psychology.",
  },
  {
    title: 'Life Circumstances',
    text: 'Season of life matters. Caregiving, grief, transitions, menopause, injury, workload—these all shift capacity. The work adapts to the season, not the other way around.',
  },
  {
    title: 'Subconscious & Belief Systems',
    text: 'Much of what drives behavior lives below awareness. Conditioning, early experiences, and inherited patterns quietly influence motivation, self-worth, and consistency. This is why effort alone doesn\'t create lasting change.',
  },
];

const VIP_TAB_INFO = {
  'start-here': 'First steps — your intake forms and what to expect.',
  video: 'Watch & practice the routines we work through together.',
  coaching: 'The Four Pillars, unfolded lesson by lesson.',
  journal: 'Your plan & notes from our sessions — all in one place.',
  'jills-corner': 'Where we work together, session by session.',
  'reading-room': 'A private library of readings & resources to explore.',
};

const VipSwitcher = ({ active }) => (
  <div className="mb-8">
    <div className="flex justify-center">
      <div className="inline-flex flex-wrap justify-center bg-white border border-stone-200 rounded-full p-1" data-testid="vip-switcher">
    <Link
      to="/programs/vip/start-here"
      className={`inline-flex items-center gap-2 px-5 py-2 rounded-full font-body text-sm transition-colors ${
        active === 'start-here' ? 'bg-forest text-cream' : 'text-stone-600 hover:text-forest'
      }`}
      data-testid="switch-start-here"
    >
      <Flag size={15} /> Start Here
    </Link>
    <Link
      to="/programs/vip"
      className={`inline-flex items-center gap-2 px-5 py-2 rounded-full font-body text-sm transition-colors ${
        active === 'video' ? 'bg-forest text-cream' : 'text-stone-600 hover:text-forest'
      }`}
      data-testid="switch-video-library"
    >
      <Play size={15} /> Video Library
    </Link>
    <Link
      to="/programs/vip/coaching"
      className={`inline-flex items-center gap-2 px-5 py-2 rounded-full font-body text-sm transition-colors ${
        active === 'coaching' ? 'bg-forest text-cream' : 'text-stone-600 hover:text-forest'
      }`}
      data-testid="switch-coaching"
    >
      <Compass size={15} /> Coaching
    </Link>
    <Link
      to="/programs/vip/journal"
      className={`inline-flex items-center gap-2 px-5 py-2 rounded-full font-body text-sm transition-colors ${
        active === 'journal' ? 'bg-forest text-cream' : 'text-stone-600 hover:text-forest'
      }`}
      data-testid="switch-journal"
    >
      <NotebookPen size={15} /> Journal
    </Link>
    <Link
      to="/programs/vip/jills-corner"
      className={`inline-flex items-center gap-2 px-5 py-2 rounded-full font-body text-sm transition-colors ${
        active === 'jills-corner' ? 'bg-forest text-cream' : 'text-stone-600 hover:text-forest'
      }`}
      data-testid="switch-jills-corner"
    >
      <Sparkles size={15} /> Jill's Corner
    </Link>
    <Link
      to="/programs/vip/reading-room"
      className={`inline-flex items-center gap-2 px-5 py-2 rounded-full font-body text-sm transition-colors ${
        active === 'reading-room' ? 'bg-forest text-cream' : 'text-stone-600 hover:text-forest'
      }`}
      data-testid="switch-reading-room"
    >
      <BookOpen size={15} /> Reading Room
    </Link>
      </div>
    </div>
    {VIP_TAB_INFO[active] && (
      <p className="text-center font-body text-sm text-stone-500 mt-3" data-testid="vip-tab-subtitle">
        {VIP_TAB_INFO[active]}
      </p>
    )}
  </div>
);

export { VipSwitcher };

const CoachingPage = () => {
  const { user, hasAccess, loading, refreshUser } = useAuth();
  const [openId, setOpenId] = useState(1);

  useEffect(() => {
    if (user?.id) refreshUser(user.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-stone-500 font-body">Loading…</div>
      </div>
    );
  }

  if (!user || !hasAccess('vip')) {
    return (
      <div className="min-h-screen bg-cream py-16 px-4" data-testid="coaching-access-gate">
        <div className="max-w-2xl mx-auto text-center">
          <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-10">
            <ArrowLeft size={16} /> Back to Programs
          </Link>
          <div className="bg-[#f5efe4] border border-stone-200 rounded-2xl p-10 md:p-14">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-forest/10 text-forest mb-6">
              <Crown size={28} />
            </div>
            <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em] mb-3">Private Members Area</p>
            <h1 className="font-heading text-3xl md:text-4xl text-forest mb-4">Coaching · The Four Pillars</h1>
            <p className="text-stone-600 font-body leading-relaxed mb-8">
              A private coaching space for Jill's clients — the Four Pillars for creating a path to wellness, unfolded lesson by lesson.
            </p>
            <a
              href="mailto:heyjbennett@yahoo.ca?subject=V.I.P. Membership Inquiry"
              className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-6 py-3 rounded-full hover:bg-forest-dark transition-colors"
              data-testid="coaching-inquiry-link"
            >
              Inquire about V.I.P. <ChevronRight size={16} />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream py-12 px-4" data-testid="coaching-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-8">
          <ArrowLeft size={16} /> Back to Programs
        </Link>

        <VipSwitcher active="coaching" />

        {/* Header / intro */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Compass size={16} className="text-[#8a6a5a]" />
            <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em]">Coaching · Will You Give It A Year?</p>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-forest mb-4">The Four Pillars for Creating a Path to Wellness</h1>
          <p className="text-stone-600 font-body leading-relaxed mb-4 max-w-2xl">
            Most people don't struggle with wellness because they don't care. They struggle because they don't know where to
            begin. These Pillars aren't rules or goals — they're reference points. Places to gently bring attention when life
            feels bumpy, heavy, or out of sync.
          </p>
          <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-6 text-sm font-body text-stone-600 leading-relaxed">
            <p className="mb-1"><span className="text-forest font-medium">Everything is connected:</span></p>
            <p>The Body affects The Mind → The Mind shapes The Emotional → The Emotional moves The Energetics</p>
            <p>and the Energetics circles back to nourish The Body.</p>
          </div>
          {PILLARS_IMG ? (
            <img src={PILLARS_IMG} alt="The Four Pillars" className="w-full max-w-md mx-auto rounded-2xl mb-6" data-testid="coaching-wheel" />
          ) : (
            <div className="w-full max-w-md mx-auto rounded-2xl mb-6 border-2 border-dashed border-stone-300 bg-white/60 p-8 text-center" data-testid="coaching-graphic-placeholder">
              <p className="font-body text-sm text-stone-400">Your Four Pillars graphic will appear here.</p>
            </div>
          )}
          <div className="bg-forest text-cream rounded-2xl p-6 md:p-8 mb-6" data-testid="rev-program-intro">
            <p className="text-xs font-body uppercase tracking-[0.25em] text-cream/60 mb-2">The REV Program</p>
            <h2 className="font-heading text-2xl md:text-3xl mb-4">Regulate · Elevate · Vitalize</h2>
            <div className="space-y-3 font-body text-sm md:text-base leading-relaxed text-cream/90">
              <p>
                REV isn't about doing more. It's about coming home to yourself — and letting the most strained parts of
                you be gently supported until the whole system begins to regulate.
              </p>
              <p>
                This is the streamlined heart of my <span className="italic">9 Pillars</span> work, gathered into four
                living layers — <span className="text-cream font-medium">The Body</span>,{' '}
                <span className="text-cream font-medium">The Mind</span>,{' '}
                <span className="text-cream font-medium">The Emotional</span> and{' '}
                <span className="text-cream font-medium">The Energetic</span>. Nothing has been lost; it's simply been
                distilled so it's easier to feel where you are, and where your system is quietly asking for support.
              </p>
              <p>
                The four pillars aren't a checklist or a set of rules. They're reference points. Instead of asking
                "What should I fix?", REV invites a softer, truer question: <span className="italic">"Where am I asking
                for support right now?"</span>
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mt-6">
              <div className="bg-cream/10 rounded-xl p-4">
                <p className="font-heading text-lg text-cream mb-1">Regulate</p>
                <p className="font-body text-xs text-cream/80 leading-relaxed">Settle the nervous system and create safety in the body, so real change becomes possible.</p>
              </div>
              <div className="bg-cream/10 rounded-xl p-4">
                <p className="font-heading text-lg text-cream mb-1">Elevate</p>
                <p className="font-body text-xs text-cream/80 leading-relaxed">Bring awareness and intention to your thoughts, emotions and choices — and lift how you meet yourself.</p>
              </div>
              <div className="bg-cream/10 rounded-xl p-4">
                <p className="font-heading text-lg text-cream mb-1">Vitalize</p>
                <p className="font-body text-xs text-cream/80 leading-relaxed">Reconnect to your energy, your aliveness and the fullest expression of you.</p>
              </div>
            </div>
            <p className="font-accent italic text-cream/90 mt-6">
              There's no rush here. Give it the time it asks for — a season, or a year — and let the work become less
              about discipline and more about attunement. This is how I changed my own life. It works.
            </p>
          </div>
          <div className="bg-[#f5efe4] border border-stone-200 rounded-2xl p-5">
            <h2 className="font-heading text-xl text-forest mb-2">The Purpose of the Pillars</h2>
            <p className="text-stone-600 font-body text-sm leading-relaxed mb-2">
              The Pillars are not meant to be worked on all at once. They are meant to be noticed. Rather than asking
              "What should I fix?" they invite a different question: <span className="italic">"Where is my system asking for support right now?"</span>
            </p>
            <p className="font-accent italic text-forest">"When the most strained areas are supported, the entire system begins to regulate."</p>
          </div>
        </motion.div>

        <FourPillarsCheckIn />

        {/* Pillar windows */}
        <div className="space-y-3" data-testid="coaching-lessons">
          {lessons.map((l) => {
            const open = openId === l.num;
            const s = CAT_STYLES[l.category] || CAT_STYLES.Physical;
            return (
              <div key={l.num} className={`bg-white rounded-2xl border ${open ? s.ring : 'border-stone-200'} overflow-hidden transition-colors`} data-testid={`coaching-lesson-${l.num}`}>
                <button
                  onClick={() => setOpenId(open ? null : l.num)}
                  className="w-full px-5 md:px-6 py-4 flex items-center justify-between gap-4 text-left hover:bg-stone-50 transition-colors"
                  data-testid={`coaching-lesson-toggle-${l.num}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full ${s.badge} text-white flex items-center justify-center font-heading flex-shrink-0`}>
                      {l.num}
                    </div>
                    <div>
                      <h3 className="font-heading text-lg md:text-xl text-forest leading-tight">{l.title}</h3>
                      <span className={`inline-block mt-1 text-[10px] font-body uppercase tracking-wider px-2 py-0.5 rounded-full ${s.chip}`}>{l.categoryLabel}</span>
                    </div>
                  </div>
                  <ChevronDown size={20} className={`text-forest flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {open && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}>
                      <div className={`px-5 md:px-6 pb-6 pt-1 ${s.bg}`}>
                        {l.body.map((p, i) => (
                          <p key={i} className="font-body text-stone-700 text-sm leading-relaxed mb-3">{p}</p>
                        ))}

                        {l.list && (
                          <ol className="space-y-2 mb-4">
                            {l.list.map((item, i) => (
                              <li key={i} className="flex gap-3">
                                <span className={`flex-shrink-0 w-6 h-6 rounded-full ${s.badge} text-white text-xs flex items-center justify-center font-body`}>{i + 1}</span>
                                <p className="font-body text-sm text-stone-700 leading-snug">
                                  <span className="font-medium text-forest">{item.h}</span>{item.t ? <>: {item.t}</> : null}
                                </p>
                              </li>
                            ))}
                          </ol>
                        )}

                        {l.support && (
                          <div className="bg-white/70 border border-stone-200 rounded-xl p-4 mb-3">
                            <p className="text-[10px] font-body uppercase tracking-wider text-stone-500 mb-1">Support may include</p>
                            <p className="font-body text-sm text-stone-700 leading-relaxed">{l.support}</p>
                          </div>
                        )}

                        {l.tags && (
                          <div className="flex flex-wrap gap-2">
                            {l.tags.map((t) => (
                              <span key={t} className={`text-xs font-body px-3 py-1 rounded-full ${s.chip}`}>{t}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Three influences */}
        <div className="mt-12" data-testid="coaching-influences">
          <h2 className="font-heading text-2xl md:text-3xl text-forest mb-2">The Three Influences That Shape Every Pillar</h2>
          <p className="font-body text-stone-600 text-sm leading-relaxed mb-5 max-w-2xl">
            Wellness does not exist in a vacuum. This is where compassion and a true holistic view can be very beneficial — and really very kind to self.
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            {influences.map((inf) => (
              <div key={inf.title} className="bg-white border border-stone-200 rounded-2xl p-5">
                <h3 className="font-heading text-lg text-forest mb-2">{inf.title}</h3>
                <p className="font-body text-sm text-stone-600 leading-relaxed">{inf.text}</p>
              </div>
            ))}
          </div>
          <div className="bg-forest text-cream rounded-2xl p-6 mt-6">
            <p className="font-accent italic text-lg leading-relaxed">
              INTENTION is 9/10th of the law! When we set intentions... everything else arranges itself to fall into alignment
              with the primary intention for our highest good.
            </p>
          </div>
        </div>

        {/* One-year journey */}
        <div className="mt-12 bg-[#f5efe4] border border-stone-200 rounded-2xl p-6 md:p-8" data-testid="coaching-one-year">
          <h2 className="font-heading text-2xl md:text-3xl text-forest mb-3">How This Becomes a One-Year Journey</h2>
          <p className="font-body text-stone-700 text-sm leading-relaxed mb-3">
            Are you willing to give it a year? This is TRULY how I changed my life... this! This process here. It's why I share
            it. It works.
          </p>
          <p className="font-body text-stone-700 text-sm leading-relaxed mb-3">
            This framework isn't meant to be rushed — it is designed for your highest goals of longevity. Over a year, each
            pillar can be explored, revisited, and re-integrated as life evolves. The work becomes less about discipline and
            more about attunement. Less about fixing and more about listening. Less about pushing and more about stability and trust.
          </p>
          <p className="font-accent italic text-forest">
            "The smoothness of the wheel improves not when everything is perfect — but when the most strained areas are gently supported."
          </p>
        </div>
      </div>
    </div>
  );
};

export default CoachingPage;
